import type { Node, Scene } from '../models/scene';
import { resolveProjectedPositions, type ProjectedLayout } from './projectedLayout';

const ANNOTATION_TYPES = new Set(['text', 'sticky-note', 'freehand', 'image', 'pdf', 'frame']);

/** Only document-owned presentation can accompany a permissioned graph. */
export function isPresentationAnnotation(node: Node): boolean {
  const attrs = node.attrs ?? {};
  return ANNOTATION_TYPES.has(node.type)
    && node.lockOwner !== 'projection'
    && !attrs.folderRecordId
    && !attrs.positionIds;
}

/**
 * Projection ownership protects canonical facts, but authored placement is
 * still document presentation and must remain movable/collaborative.
 */
export function isProjectionOwnedNode(node: Node): boolean {
  return node.locked === true && node.lockOwner === 'projection';
}

/**
 * Automatic source-backed charts own the placement of projected cards.
 * Keep this separate from editability: cards must remain selectable and
 * available to their domain actions while their diagram position is fixed.
 */
export function isProjectedNodePlacementLocked(
  node: Node | undefined,
  lockProjectedNodePlacement: boolean,
): boolean {
  return lockProjectedNodePlacement && !!node && isProjectionOwnedNode(node);
}

export function canEditProjectedNode(node: Node, canWrite: boolean, projectionLocked: boolean): boolean {
  return canWrite && (!projectionLocked || isPresentationAnnotation(node) || isProjectionOwnedNode(node));
}

export type ProjectionPresentation = Pick<Scene, 'nodes' | 'edges'> & {
  projectedLayout?: ProjectedLayout;
  customFieldOverrides?: ReadonlyMap<string, Record<string, string | number>>;
};

export function composeProjectedScene(projected: Scene, presentation: ProjectionPresentation): Scene {
  const canonicalIds = new Set(projected.nodes.map((node) => node.id));
  const annotations = presentation.nodes.filter((node) => isPresentationAnnotation(node) && !canonicalIds.has(node.id));
  const annotationIds = new Set(annotations.map((node) => node.id));
  const canonicalEdgeIds = new Set(projected.edges.map((edge) => edge.id));
  const presentationById = new Map(presentation.nodes.map((node) => [node.id, node] as const));
  const customPositions = presentation.projectedLayout?.mode === 'custom'
    ? resolveProjectedPositions(projected.nodes, presentation.projectedLayout) : undefined;
  // A projection owns canonical fields and relationships, but authored card
  // placement is presentation state. Carry only geometry across the
  // projection boundary so connectors are computed from the same scene that
  // the user sees. In particular, do not let a historical preview fall back
  // to the projection's freshly laid-out coordinates.
  const composedNodes = projected.nodes.map((node) => {
    const authored = customPositions?.get(node.id)
      ?? (presentation.projectedLayout ? undefined : presentationById.get(node.id));
    const authoredOverrides = presentation.customFieldOverrides?.get(node.id)
      ?? authored?.attrs?.customFieldOverrides;
    if (!authored && !authoredOverrides) return node;
    const withPresentationOverrides = authoredOverrides && typeof authoredOverrides === 'object'
      ? { ...node, attrs: { ...(node.attrs ?? {}), customFieldOverrides: authoredOverrides } }
      : node;
    const x = typeof authored?.x === 'number' && Number.isFinite(authored.x) ? authored.x : node.x;
    const y = typeof authored?.y === 'number' && Number.isFinite(authored.y) ? authored.y : node.y;
    if (x === node.x && y === node.y) return withPresentationOverrides;
    return { ...withPresentationOverrides, x, y };
  });
  return {
    ...projected,
    nodes: [...composedNodes, ...annotations],
    // Document connectors cannot supply canonical relationships or reveal
    // an endpoint withheld by the caller's graph projection.
    edges: [...projected.edges.map((edge) => {
      if (!customPositions) return edge;
      // Tree routes are derived coordinates, not reporting relationships.
      const attrs = { ...edge.attrs };
      delete attrs.layoutPoints;
      delete attrs.layoutSource;
      delete attrs.layoutDirection;
      attrs.projectedLayout = 'custom';
      return { ...edge, attrs };
    }), ...presentation.edges.filter((edge) =>
      annotationIds.has(edge.from) && annotationIds.has(edge.to) && !canonicalEdgeIds.has(edge.id))],
  };
}
